<template>
    <loading v-if="loading || term == null"></loading>
    <div class="page--period" v-else>

        <header class="page-header page-header--wide">
            <h1>{{ salutation }} <small>{{ subheading }}</small></h1>
            <p>{{ intro }}</p>
            <p class="note note--info" v-if="!isCurrent && !isTeacher"><strong>Let op:</strong> vraag eerst even aan je docent of je vast aan deze opdrachten mag werken. Ga naar <router-link :to="{ name: 'period' }">jouw opdrachtenlijst</router-link> om de opdrachten van de huidige periode te bekijken.
            <hr>
        </header>

        <h1 class="banner" v-if="assignments.length"><span>Welke wil je het eerst maken?</span></h1>

        <transition-group tag="div" class="assignments" name="insertion-bottom" appear>
            <assignment-route v-for="assignment in assignments" :assignment="assignment" :progress="progress(assignment)" :user="user" :key="assignment.id" :disableIfUnfinished="isInspectingStudent" :ignoreProgress="ignoreProgress" :disable="isDemo && !assignment.demo" ></assignment-route>
        </transition-group>

        <div class="assignments" v-if="retakeAssignments.length > 0 && !ignoreProgress && isCurrent">
            <hr>
            <h1 class="banner"><span>Opnieuw inleveren</span></h1>
            <transition-group tag="div" name="insertion-bottom" appear>
                <assignment-route v-for="assignment in retakeAssignments" :assignment="assignment" :user="user" :key="assignment.id" :disableIfUnfinished="isInspectingStudent" :disable="isDemo && !assignment.demo"   :progress="progress(assignment)"></assignment-route>
            </transition-group>
        </div>

        <div class="assignments" v-if="overdueAssignments.length > 0 && !ignoreProgress && isCurrent">
            <hr>
            <h1 class="banner"><span>Nog niet afgemaakt</span></h1>
            <transition-group tag="div" name="insertion-bottom" appear>
                <assignment-route v-for="assignment in overdueAssignments" :assignment="assignment" :user="user" :key="assignment.id" :disableIfUnfinished="isInspectingStudent" :disable="isDemo && !assignment.demo"   :progress="progress(assignment)"></assignment-route>
            </transition-group>
        </div>

    </div>
</template>


<script>

import Loading from 'js/components/elements/Loading.vue';
import AssignmentRoute from 'js/components/assignment/Route.vue';
import { canTeach } from 'js/api/auth';
import { getTermForPeriod, getProgressForUser, getCurrentGroupPeriodForGroup, getGroupTermsForGroup } from 'js/vuex/getters'

export default {
    name: 'Period',
    components: { AssignmentRoute, Loading },
    props: {
        user: {},
        group: {},
        loading: {
            type: Boolean,
            default: false,
        },
        isInspectingStudent: {
            type: Boolean,
            default: false,
        },
        isDemo: {
            type: Boolean,
            default: false,
        },
        ignoreProgress: {
            type: Boolean,
            default: false,
        }
    },
    vuex: {
        getters: {
            getTermForPeriod,
            getProgressForUser,
            getCurrentGroupPeriodForGroup,
            getGroupTermsForGroup,
        },
        actions: {
            showTerm: ({ dispatch }) => {
                dispatch('SHOW_MODAL', 'Term')
            },
        }
    },
    methods: {
        progress: function(assignment) {
            return this.userProgressions.find( p => p.assignment === assignment.id )
        },
        assignmentsForPeriods: function(groupPeriods, includeOptional) {
            return groupPeriods
                .map( id => this.$store.state.groups.periods[id] ) // map id to period
                .reduce( (list, p) => list.concat(p.groupAssignments), [] ) // flatmap to groupAssignmentsIds
                .map( id => this.$store.state.groups.assignments[id] ) // map id to GroupAssignment
                .filter( ga => !ga.optional || includeOptional )
                .map( ga => this.$store.state.assignments.storage[ga.assignment[0]] ) // map id to assignment
        },
    },
    computed: {
        isCurrent: function() {
            const current = this.getCurrentGroupPeriodForGroup(this.group.id)
            return current != null && current.id === this.groupPeriod.id
        },
        term: function() {
            if (this.groupTerm == null) { return null }
            return this.$store.state.terms.storage[ this.groupTerm.term[0] ]
        },
        period: function() {
            if (this.groupPeriod == null) { return null }
            const id = this.groupPeriod.period[0]
            return this.$store.state.periods.storage[ id ]
        },
        groupTerm: function() {
            if (this.groupPeriod == null) { return null }
            const groupTerms = this.getGroupTermsForGroup(this.group.id)
            return groupTerms.find( t => t.groupPeriods.indexOf( this.groupPeriod.id ) !== -1)
        },
        groupPeriod: function() {
            if ( this.$route.params.periodId == null ) {
                return this.getCurrentGroupPeriodForGroup(this.group.id)
            }

            return this.$store.state.groups.periods[ this.$route.params.periodId ]
        },
        previousGroupPeriods: function() {
            if ( this.groupPeriod == null ) {
                return []
            }
            // note that groupPeriods must be sorted!
            const periodIndex = this.groupTerm.groupPeriods.indexOf( this.groupPeriod.id )
            return this.groupTerm.groupPeriods.slice(0, periodIndex)
        },

        assignments: function() {
            return (this.period == null) ? [] :
                this.groupPeriod.groupAssignments
                    .map( id => this.$store.state.groups.assignments[id] )
                    .filter( ga => !ga.optional )
                    .map( ga => this.$store.state.assignments.storage[ga.assignment[0]] )
        },
        overdueAssignments: function() {

            return this.assignmentsForPeriods(this.previousGroupPeriods)
                .filter( assignment => {
                    // only pick assignments that have not been finished
                    const progress = this.progress(assignment)
                    return progress == null || progress.finished == null
                })
        },
        retakeAssignments: function() {
            return this.assignmentsForPeriods(this.previousGroupPeriods)
                .filter( assignment => (this.progress(assignment) || {}).isRetaking )
        },

        userProgressions: function() {
            return this.getProgressForUser( this.user.id )
                .filter(p => p.group === this.group.id )
        },

        salutation: function() {
            return this.isTeacher || !this.isCurrent ? (this.term == null ? '' : this.term.title) : 'Hallo ' + this.user.firstname
        },
        subheading: function() {
            return this.isTeacher || !this.isCurrent ? (this.period == null ? '' : this.period.title) : ''
        },
        isTeacher: function() {
            return canTeach(this.$store.state.users.current)
        },
        viewedTermIntro: function() {
            return this.$store.state.terms.viewed
        },
        intro: function() {
            return (this.period != null) ? this.period.intro : 'Er zijn geen nieuwe opdrachten voor jou beschikbaar.'
        },
        shouldShowTermintro: function() {
            if (this.viewedTermIntro || this.groupTerm == null || this.isTeacher || this.loading || !this.isCurrent ) {
                return false
            }

            const startedAssignments = this.assignmentsForPeriods(this.groupTerm.groupPeriods, true)
                .map(this.progress)
                .filter( p => p != null )

            return startedAssignments.length === 0
        },
    },

    watch: {
        shouldShowTermintro: {
            handler: function(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    this.showTerm()
                }
            },
            immediate: true
        }
    }
}
</script>


<style lang="scss">

    @import 'global';

    .page--period {
        @include clearfix();

        .assignments {
            text-align: center;
            @include clearfix();
        }

    }


</style>



// WEBPACK FOOTER //
// Period.vue?6e2bad16